import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X } from 'lucide-react';

export default function EditPGModal({ isOpen, onClose, pg, onSave, loading }) {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    defaultRent: 5000,
    defaultDeposit: 10000,
    depositRefundablePercent: 100
  });

  useEffect(() => {
    if (pg) {
      setFormData({
        name: pg.name,
        address: pg.address,
        defaultRent: pg.defaultRent,
        defaultDeposit: pg.defaultDeposit,
        depositRefundablePercent: pg.depositRefundablePercent
      });
    }
  }, [pg]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full" data-testid="edit-pg-modal">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 font-heading">Edit PG Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="close-edit-pg-modal"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">PG Name *</Label>
            <Input
              placeholder="e.g., Sharma PG"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="h-12 rounded-lg"
              required
              data-testid="edit-pg-name"
            />
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Address *</Label>
            <Input
              placeholder="Full address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="h-12 rounded-lg"
              required
              data-testid="edit-pg-address"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Default Rent (₹) *</Label>
              <Input
                type="number"
                value={formData.defaultRent}
                onChange={(e) => setFormData({ ...formData, defaultRent: parseFloat(e.target.value) })}
                className="h-12 rounded-lg"
                required
                data-testid="edit-pg-rent"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Default Deposit (₹) *</Label>
              <Input
                type="number"
                value={formData.defaultDeposit}
                onChange={(e) => setFormData({ ...formData, defaultDeposit: parseFloat(e.target.value) })}
                className="h-12 rounded-lg"
                required
                data-testid="edit-pg-deposit"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 h-12 rounded-xl"
              disabled={loading}
              data-testid="cancel-edit-pg"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 h-12 rounded-xl bg-primary"
              disabled={loading}
              data-testid="save-edit-pg"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
